import { ASSET_IMAGES } from '../assets/images';

export interface ProductSEO {
  metaTitle?: string;
  metaDescription?: string;
  keywords?: string;
  tags?: string[];
}

export interface Product {
  id: string;
  name: string;
  category: 'water-purification' | 'milk-atm' | 'water-vending' | 'cooking-oil' | 'pasteurizers' | 'spares';
  priceKsh: number;
  originalPriceKsh?: number;
  inStock: boolean;
  image: string;
  description: string;
  tagline: string;
  capacity: string;
  material: string;
  power: string;
  features: string[];
  specs: { label: string; value: string }[];
  seo?: ProductSEO;
}

export const PRODUCTS: Product[] = [
  {
    id: 'milk-pasteurizer-300l',
    name: '300 Liters 3-Phase Commercial Milk Pasteurizer',
    category: 'pasteurizers',
    priceKsh: 340000,
    originalPriceKsh: 350000,
    inStock: true,
    image: ASSET_IMAGES.milkPasteurizer,
    tagline: 'High-efficiency jacketed pasteurization tank with precision temperature automation.',
    description: 'Fabricated from food-grade Stainless Steel 304 with mirror sanitary finish. Features heavy-duty top geared motor agitator, digital PID temperature controls, water jacket heating, and sanitary tri-clamp discharge butterfly valve.',
    capacity: '300 Liters / Batch',
    material: 'Food-Grade Stainless Steel AISI 304 (Inner & Outer Jacket)',
    power: '3-Phase 415V / 9kW Heating Elements',
    features: [
      'Digital PID thermostatic temperature controller',
      'Geared top agitator with sanitary paddle',
      'Double jacket water heating and cold-water cooling circulation',
      'Food-grade butterfly sanitary drain valve',
      'Integrated temperature safety cutoff probe',
      '1 Year Saints Tech Warranty & Free On-site Commissioning'
    ],
    specs: [
      { label: 'Batch Volume', value: '300 Litres' },
      { label: 'Heating Method', value: 'Electric Water-Jacket Immersion' },
      { label: 'Agitator Speed', value: '36 RPM Food Grade Gear Motor' },
      { label: 'Operating Temp Range', value: 'Ambient to 95°C' },
      { label: 'Insulation', value: 'High-Density Polyurethane Foam' },
      { label: 'Discharge Valve', value: '1.5" Sanitary Tri-Clamp' }
    ]
  },
  {
    id: 'milk-atm-100l',
    name: 'Milk ATM Machine 100L Digital Dispenser',
    category: 'milk-atm',
    priceKsh: 75000,
    inStock: true,
    image: ASSET_IMAGES.milkAtm,
    tagline: 'Automated hygienic dairy dispensing with GSM daily sales tracking.',
    description: 'Precision milk vending automated kiosk with integrated refrigeration, sanitary milk pump, digital alphanumeric keypad, and tamper-proof daily sales recording. Perfect for dairy shops, supermarkets, and local vendors.',
    capacity: '100 Liters Holding Tank',
    material: 'SS 304 Food Grade Cooling Tank & Casing',
    power: 'Single Phase 220-240V / Low Power Consumption (< 350W)',
    features: [
      'Digital PLC volume and currency dispenser (dispense from as low as KSh 10)',
      'Built-in food-grade cooling unit maintaining milk below 4°C',
      'GSM module for daily SMS sales reports and tamper alerts',
      'Automatic cleaning mode (Clean-in-Place cycle)',
      'Anti-drip electromagnetic solenoid valve',
      'Password protected administrator audit menus'
    ],
    specs: [
      { label: 'Holding Capacity', value: '100 Litres' },
      { label: 'Cooling Unit', value: 'Embraco Heavy Duty Compressor' },
      { label: 'Dispense Accuracy', value: '± 5ml (High Precision Flowmeter)' },
      { label: 'Sales Audit', value: 'GSM Real-time SMS & LCD Counter' },
      { label: 'Power Backup', value: 'UPS Compatible for constant sales' },
      { label: 'Sanitation', value: 'Automated CIP Flushing System' }
    ]
  },
  {
    id: 'milk-atm-200l',
    name: 'Milk ATM Machine 200L High-Traffic Kiosk',
    category: 'milk-atm',
    priceKsh: 125000,
    originalPriceKsh: 135000,
    inStock: true,
    image: ASSET_IMAGES.milkAtm,
    tagline: 'High volume milk dispenser for busy market centers and dairy hubs.',
    description: 'Heavy duty 200 Litre refrigerated milk dispenser equipped with double cooling coils, stainless steel sanitary pump, high-speed dispensing nozzle, and full digital sales tracking.',
    capacity: '200 Liters Storage',
    material: 'SS 304 Mirror Polish',
    power: '220V 50Hz / 450W',
    features: [
      'Dual cooling coils for rapid temperature pull-down',
      'High-speed food grade positive displacement pump',
      'GSM accounting with daily M-Pesa integration support',
      'Lockable heavy-gauge steel casing with tamper proof locks',
      '12 Months warranty with scheduled servicing'
    ],
    specs: [
      { label: 'Capacity', value: '200 Litres' },
      { label: 'Dispensing Speed', value: '1 Litre in 6 seconds' },
      { label: 'Refrigerant', value: 'R134a Eco-friendly' },
      { label: 'Tracking', value: 'SMS Audit + Cumulative Totalizer' }
    ]
  },
  {
    id: 'water-filling-station-3taps',
    name: '3 Taps Water Filling Station & Dispenser',
    category: 'water-vending',
    priceKsh: 75000,
    inStock: true,
    image: ASSET_IMAGES.waterStation,
    tagline: 'Heavy-duty stainless steel water filling canopy station for refill kiosks.',
    description: 'Designed for commercial purified water refill shops. Features 3 independent high-flow stainless dispensing taps, integrated rinse sink with drip drainage, digital control board, and LED illuminated canopy.',
    capacity: 'Up to 3 simultaneous bottle fills (0.5L, 1L, 5L, 10L, 20L)',
    material: 'Full Stainless Steel 304 Frame & Basin',
    power: '220V / 100W',
    features: [
      'Three dedicated dispensing taps with push-button activation',
      'Bottle rinse basin with high pressure inverted bottle rinser',
      'Integrated digital flow counting board',
      'Splash-proof tempered glass / stainless front canopy',
      'Corrosion-resistant sanitary plumbing and non-return valves'
    ],
    specs: [
      { label: 'Taps', value: '3 Food-Grade Push Taps' },
      { label: 'Basin Dimensions', value: '1200mm x 600mm x 1800mm' },
      { label: 'Rinsing Feature', value: 'Inverted High-Pressure Nozzle' },
      { label: 'Frame', value: 'Square Tube SS 304 Welded Construction' }
    ]
  },
  {
    id: 'water-filling-station-std',
    name: 'Commercial Water Filling Station (2 Taps + Rinser)',
    category: 'water-vending',
    priceKsh: 65000,
    inStock: true,
    image: ASSET_IMAGES.waterStation,
    tagline: 'Standard water refill station ideal for startup purification shops.',
    description: 'Compact and efficient stainless steel water refill booth with dual taps, inverted bottle washing station, and drain manifold. Built to meet public health standards.',
    capacity: '2 Simultaneous Fills',
    material: 'Stainless Steel 304',
    power: '220V / 60W',
    features: [
      'Twin stainless dispensing taps',
      'Integrated drain collector and rinse sink',
      'Pre-plumbed for instant hookup to RO or UV storage tanks',
      'Durable sanitary finish easy to disinfect'
    ],
    specs: [
      { label: 'Number of Outlets', value: '2 Dispensing Points' },
      { label: 'Sink Depth', value: '250mm' },
      { label: 'Plumbing', value: '1/2" Food Grade Braided Hose & PVC-U' }
    ]
  },
  {
    id: 'water-purifier-ro-500l',
    name: '500 Litres/hr Reverse Osmosis Commercial Purifier',
    category: 'water-purification',
    priceKsh: 210000,
    originalPriceKsh: 225000,
    inStock: true,
    image: ASSET_IMAGES.heroWater,
    tagline: 'Multi-stage commercial RO filtration plant for pure bottled-grade water.',
    description: 'High-performance commercial Reverse Osmosis skid plant. Features dual FRP media pre-treatment vessels (sand filter, activated carbon), micron sediment filter housings, high-pressure multistage stainless booster pump, and industrial 4040 RO membranes.',
    capacity: '500 Litres per Hour (12,000L / day)',
    material: '304 Stainless Steel Frame Skid & High-Pressure Piping',
    power: 'Single Phase 220V or 3-Phase 380V / 1.5kW High Pressure Pump',
    features: [
      'High-pressure multi-stage CNP/Shimge stainless steel pump',
      'Dual FRP pre-treatment vessels with manual or automatic multiport valves',
      'Dow Filmtec / Toray 4040 high-rejection RO membrane elements',
      'Rotameter flowmeters for permeate and concentrate streams',
      'Online digital TDS / Conductivity monitoring meter',
      'Automated high and low pressure safety protection switches'
    ],
    specs: [
      { label: 'Pure Water Output', value: '500 Liters / Hour' },
      { label: 'Salt Rejection Rate', value: '98.5% - 99.2%' },
      { label: 'Booster Pump', value: '1.5kW Multistage SS Pump' },
      { label: 'Membrane Spec', value: 'Industrial 4040 RO Element' },
      { label: 'Pre-filtration', value: 'Sand Filter + Activated Carbon + 5µm Cartridge' }
    ]
  },
  {
    id: 'water-purifier-ro-1000l',
    name: '1000 Litres/hr Industrial Reverse Osmosis Purification Plant',
    category: 'water-purification',
    priceKsh: 380000,
    inStock: true,
    image: ASSET_IMAGES.heroWater,
    tagline: 'Heavy-duty industrial water bottling and commercial purification plant.',
    description: 'Engineered for commercial water packaging factories, schools, beverage producers, and community water supply stations. Built with twin 8040 or quad 4040 membranes, automated backwash valves, and UV sterilization.',
    capacity: '1,000 Litres per Hour (24,000L / day)',
    material: 'Stainless Steel 304 Skid with Heavy-duty FRP Vessels',
    power: '3-Phase 415V / 2.2kW High Pressure Pump',
    features: [
      'Industrial PLC control panel with automated flush cycle',
      'Ultraviolet (UV) inline sterilizer for complete pathogen elimination',
      'Heavy-duty dosing pump for anti-scalant dosing',
      'Dual pressure gauges and digital conductivity monitor',
      'Comprehensive on-site installation, pipework and water testing included'
    ],
    specs: [
      { label: 'Capacity', value: '1,000 Litres / Hour' },
      { label: 'Membranes', value: 'Dual 8040 High Rejection Elements' },
      { label: 'Pretreatment', value: '1465 FRP Vessels with Runxin automatic heads' },
      { label: 'Sterilization', value: 'Inline 55W Philips UV Chamber' }
    ]
  },
  {
    id: 'cooking-oil-atm-50l',
    name: 'Cooking Oil ATM / Salad ATM 50L Single Nozzle',
    category: 'cooking-oil',
    priceKsh: 65000,
    inStock: true,
    image: ASSET_IMAGES.cookingOil,
    tagline: 'High-precision cooking oil dispenser that eliminates packaging waste.',
    description: 'Specialized food-grade oil dispensing kiosk with positive displacement oil gear pump, heated holding compartment for cold weather viscosity, digital pricing computer, and anti-drip nozzle.',
    capacity: '50 Liters Reservoir Tank',
    material: 'Food-Grade Stainless Steel 304 Dispensing Zone & Exterior',
    power: '220V 50Hz / 180W',
    features: [
      'High-precision food grade positive displacement metering',
      'Dispense any amount by money (e.g. KSh 20, KSh 50, KSh 100) or exact volume',
      'Built-in heating element to keep cooking oil fluid during cold mornings',
      'Anti-drip valve ensures spotless counter and zero spillage',
      'GSM remote tracking for inventory levels and sales audits'
    ],
    specs: [
      { label: 'Capacity', value: '50 Litres' },
      { label: 'Oil Types', value: 'Vegetable Cooking Oil, Salad Oil, Sunflower Oil' },
      { label: 'Metering Type', value: 'Positive Displacement Gear Meter' },
      { label: 'Heating Aid', value: 'Thermostatic Low-Wattage Pad' }
    ]
  },
  {
    id: 'cooking-oil-atm-100l',
    name: 'Cooking Oil ATM 100L Twin Nozzle Dispenser',
    category: 'cooking-oil',
    priceKsh: 115000,
    originalPriceKsh: 125000,
    inStock: true,
    image: ASSET_IMAGES.cookingOil,
    tagline: 'Dual-variety cooking oil kiosk for busy wholesale and retail stores.',
    description: 'Dispense two types of cooking oil simultaneously (e.g., pure sunflower oil & blended vegetable cooking oil) with independent digital controllers and twin food-grade pumps.',
    capacity: '100 Liters (50L + 50L Dual Chamber)',
    material: 'Stainless Steel 304 Mirror Casing',
    power: '220V / 280W',
    features: [
      'Twin independent dispensing heads and keypads',
      'Separate storage tanks and pumps for two distinct oil grades',
      'Anti-drip nozzles and spill-capture drain tray',
      'Tamper-proof financial counters and SMS daily reports'
    ],
    specs: [
      { label: 'Capacity', value: '2 x 50 Litres' },
      { label: 'Dispense Heads', value: '2 Independent Taps' },
      { label: 'Power', value: 'Standard 240V Kenyan Plug' }
    ]
  },
  {
    id: 'uv-water-sterilizer-55w',
    name: '55W Industrial Ultraviolet (UV) Water Sterilizer',
    category: 'spares',
    priceKsh: 38000,
    inStock: true,
    image: ASSET_IMAGES.heroWater,
    tagline: '99.99% microbial eradication chamber for pure water bottling lines.',
    description: 'Stainless steel 304 reaction chamber fitted with high-efficiency Philips/Germicidal UV-C bulb and high-transmittance quartz sleeve. Eliminates bacteria, viruses, and cysts without adding chemicals.',
    capacity: 'Flow rate up to 12 GPM (2,700 L/hr)',
    material: 'Stainless Steel 304 Chamber',
    power: '220V Electronic Ballast with Lamp Failure Alarm',
    features: [
      'Destroys 99.99% of bacteria, E. coli, salmonella, and waterborne cysts',
      'Electronic ballast with audible and visual lamp status alert',
      'Premium quartz sleeve for maximum UV transmission',
      '1" Male BSP inlet and outlet connections'
    ],
    specs: [
      { label: 'Bulb Wattage', value: '55 Watts' },
      { label: 'Lamp Lifespan', value: '9,000 Hours (Approx. 1 Year continuous)' },
      { label: 'Chamber Material', value: 'AISI 304 Polished' }
    ]
  },
  {
    id: 'chemical-dosing-pump',
    name: 'Digital Chemical Dosing Pump (Anti-Scalant / Chlorine)',
    category: 'spares',
    priceKsh: 32000,
    inStock: true,
    image: ASSET_IMAGES.heroWater,
    tagline: 'Precision electromagnetic diaphragm dosing pump for water treatment plants.',
    description: 'Essential for commercial Reverse Osmosis systems to prevent silica/calcium scaling on membranes or for precise chlorine disinfection in raw water reservoirs.',
    capacity: '5.0 Litres/hr at 7 Bar Pressure',
    material: 'PVDF Liquid End & Ceramic Ball Valves',
    power: '220V 50Hz',
    features: [
      'Digital stroke rate frequency and stroke length adjustment',
      'Chemical-resistant PVDF head compatible with acid, chlorine, and anti-scalants',
      'Foot valve, injection valve, and suction/discharge tubing included'
    ],
    specs: [
      { label: 'Max Flow', value: '5 L/h' },
      { label: 'Max Pressure', value: '7 Bar' },
      { label: 'Dosing Type', value: 'Electromagnetic Diaphragm' }
    ]
  },
  {
    id: 'ro-membrane-4040',
    name: 'Dow Filmtec / Vontron 4040 Commercial RO Membrane',
    category: 'spares',
    priceKsh: 18500,
    inStock: true,
    image: ASSET_IMAGES.heroWater,
    tagline: 'High rejection polyamide composite membrane element.',
    description: 'Industrial 4-inch diameter by 40-inch length reverse osmosis membrane. High salt rejection for brackish and borehole water purification in Kenya.',
    capacity: '2,400 Gallons per Day (GPD)',
    material: 'Polyamide Thin-Film Composite',
    power: 'Passive Element',
    features: [
      'Stabilized salt rejection 99.5%',
      'Tolerates high TDS water common in Nairobi and Rift Valley boreholes',
      'Standard 4040 dimensions fits all stainless steel and FRP housings'
    ],
    specs: [
      { label: 'Dimensions', value: '4.0" Diameter x 40" Length' },
      { label: 'Max Operating Pressure', value: '300 PSI (21 Bar)' }
    ]
  },
  {
    id: 'plc-gsm-controller-board',
    name: 'Smart Vending PLC Controller Board with GSM & Keypad',
    category: 'spares',
    priceKsh: 22000,
    inStock: true,
    image: ASSET_IMAGES.milkAtm,
    tagline: 'Replacement logic control board for Milk ATMs, Water and Salad Oil ATMs.',
    description: 'Pre-programmed micro-controller with backlit LCD display, industrial 16-key keypad, pulse input for flow sensors, relay outputs for pump & solenoid, and integrated GSM SMS module.',
    capacity: 'Universal compatible with Saints Tech & most Kenyan vending machines',
    material: 'Industrial PCB with conformal coating',
    power: '12V DC / 240V AC Adaptor',
    features: [
      'Accurate calibration for milk, water, or oil densities',
      'Daily sales audit via password and SMS notifications',
      'Connects directly to flow sensor, solenoid valve, and float switches'
    ],
    specs: [
      { label: 'Display', value: '20x4 Alphanumeric Backlit LCD' },
      { label: 'GSM Band', value: 'Quad-band 850/900/1800/1900 MHz' }
    ]
  }
];

export interface SpareService {
  title: string;
  category: string;
  description: string;
  turnaround: string;
}

export const SERVICES_LIST: SpareService[] = [
  {
    title: 'Routine Reverse Osmosis Membrane Chemical CIP Cleaning',
    category: 'Water Plant Servicing',
    description: 'Restores flux rate and lowers operating pressure by removing scaling, biological fouling, and organic matter from RO membranes using food-safe acid and alkaline CIP chemicals.',
    turnaround: 'Same day (3-4 hours)'
  },
  {
    title: 'Water Purification Plant Sand & Carbon Media Replacement',
    category: 'Media Regeneration',
    description: 'Complete replacement of depleted activated carbon, graded silica sand, and manganese greensand in FRP vessels for optimal taste, odor, and turbidity removal.',
    turnaround: '1 Business Day'
  },
  {
    title: 'Milk ATM Calibration, Deep Sanitation & Refrigeration Servicing',
    category: 'Dairy Maintenance',
    description: 'Precision volumetric calibration of flow meters to ensure exact dispensing, refrigerant gas top-up, compressor health check, and full CIP sanitary cycle testing.',
    turnaround: 'Same day (2-3 hours)'
  },
  {
    title: 'Water Vending Station Plumbing Overhaul & Solenoid Replacement',
    category: 'Vending Stations',
    description: 'Inspection and replacement of sticky electromagnetic solenoid valves, stainless steel push taps, drip trays, and flow sensors to eliminate drips and errors.',
    turnaround: 'Same day'
  },
  {
    title: 'Borehole & Raw Water Laboratory Physical-Chemical Testing',
    category: 'Water Quality Analysis',
    description: 'Full certified testing for pH, TDS, Hardness, Iron, Fluoride, Heavy Metals, and Coliform bacteria to custom-engineer the exact filtration plant required for KeBS certification.',
    turnaround: '48-72 Hours'
  },
  {
    title: 'Emergency On-Site Technician Call-out (Nairobi & Countrywide)',
    category: 'Emergency Dispatch',
    description: 'Our mobile team arrives fully equipped with replacement pumps, PLC boards, and fittings to restore your vending machine or purification plant quickly.',
    turnaround: 'Within 2-4 hours in Nairobi'
  }
];
